import * as Twilio from '@twilio/voice-sdk'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import TwilioController from '@/api/twilioController'
import eventBus from '@/common/bus'
import { getLogger } from 'loglevel'
import { log } from '@/lib/logging'

const logger = getLogger('@simplifi/twilio-client')

export const useTwilioDevice = defineStore('twilio-device', () => {
  const initialized = ref<boolean>(false)
  const instance = ref<Twilio.Device>()

  const audioConfig = ref({
    rtcConstraints: {
      audio: {
        autoGainControl: false,
      },
    },
  })

  async function onRegistered() {
    logger.debug('device:registered')

    // Clear any twilio warnings
    eventBus.$emit('clearTwilioSnackbar')

    if (!instance.value?.audio) {
      return
    }

    try {
      await instance.value.audio.speakerDevices.set(['communications'])
      await instance.value.audio.ringtoneDevices.set(['default', 'communications'])
    } catch (error) {
      log('Issue setting specified Twilio audio devices', {
        level: 'warn',
        error,
        context: {
          availableDevices: {
            input: instance.value?.audio.availableInputDevices,
            output: instance.value?.audio.availableOutputDevices,
            speaker: instance.value?.audio.speakerDevices.get(),
            ringtone: instance.value?.audio.ringtoneDevices.get(),
          },
        },
      })
    }
  }

  async function onTokenWillExpire() {
    logger.debug('device:onTokenWillExpire')
    await refreshToken()
  }

  async function onUnregistered() {
    logger.debug('device:unregistered')
    await refreshToken()
  }

  async function onError(error: Twilio.TwilioError.TwilioError) {
    log('Twilio device error', { level: 'error', error }).notifyUser()

    // https://www.twilio.com/docs/voice/sdks/error-codes
    switch (error.code) {
      case 20101: // Other Twilio JWT no longer valid
      case 20104: // Access Token expired or expiration date invalid
      case 31204: // Invalid JWT token
      case 31205: // JWT token expired
        await refreshToken()
        break
      case 31201: // Generic unknown error
      case 31208: // User denied access to microphone
        eventBus.$emit('showTwilioSnackbar', 'Error accessing microphone', false)
        console.error('Could not access the microphone')
        break
    }
  }

  async function init() {
    if (initialized.value) {
      return
    }

    logger.debug('device:init')

    const clientToken = await TwilioController.GetClientToken()
    instance.value = new Twilio.Device(clientToken, {
      closeProtection: true,
      //edge: 'ie1',
      codecPreferences: [Twilio.Call.Codec.Opus, Twilio.Call.Codec.PCMU],
      tokenRefreshMs: 300000, // 5 minutes
    })

    instance.value.on('registered', onRegistered)
    instance.value.on('tokenWillExpire', onTokenWillExpire)
    instance.value.on('error', onError)
    instance.value.on('unregistered', onUnregistered)
    instance.value.register()

    initialized.value = true
  }

  const TOKEN_REFRESH_MAX_RETRIES = 5
  const TOKEN_REFRESH_BASE_DELAY_MS = 1000

  async function refreshToken(retryCount = 0): Promise<void> {
    if (!instance.value) {
      throw new Error('Attempted to refresh device token without device.')
    }

    try {
      const clientToken = await TwilioController.GetClientToken()

      if (!clientToken) {
        throw new Error('Received empty token from server')
      }

      instance.value.updateToken(clientToken)
      logger.info('Twilio device token refreshed')
    } catch (error) {
      logger.error(`Failed to refresh Twilio token (attempt ${retryCount + 1}/${TOKEN_REFRESH_MAX_RETRIES})`, error)

      if (retryCount < TOKEN_REFRESH_MAX_RETRIES - 1) {
        const delay = TOKEN_REFRESH_BASE_DELAY_MS * Math.pow(2, retryCount)
        logger.info(`Retrying token refresh in ${delay / 1000}s...`)
        await new Promise((resolve) => setTimeout(resolve, delay))
        return refreshToken(retryCount + 1)
      }

      throw error
    }
  }

  return {
    init,
    initialized,
    audioConfig,
    instance: computed(() => {
      if (!instance.value) {
        throw new Error('Attempted to access device before init.')
      }
      return instance.value
    }),
  }
})
